/**
 * Spanish class content keyed by exact English DB title.
 * Used when site locale is es; English DB values remain the source of truth for admin.
 */

import { EIP_APPLICATION_SITE, EIP_APPLICATION_URL } from './eipConfig';

const EIP_HELP_SECTION_ES = {
  title: '¿Necesita ayuda para pagar la capacitación?',
  content:
    `Puede calificar para una beca EIP del Programa de Incentivo Educativo, financiado por la Oficina de Servicios para Niños y Familias. Para solicitar una aplicación puede llamar al (800) 295-9616, o visitar ${EIP_APPLICATION_SITE}`,
  link: EIP_APPLICATION_URL,
  linkLabel: 'Visitar el sitio de solicitud EIP',
};

export const CLASS_CONTENT_ES = {
  'Child Development Associate (CDA)': {
    title: 'Asociado en Desarrollo Infantil (CDA)',
    description: `Este programa consiste en 120 horas de capacitación en la primera infancia que conducen a la credencial de Asociado en Desarrollo Infantil (CDA) a través del Consejo para el Reconocimiento Profesional. Este programa es para personas que trabajan en el campo de la primera infancia con niños menores de cinco años.

Las clases se reúnen de lunes a viernes de 7:00 PM a 10:00 PM por Zoom. Tarifa del curso: $2,500.`,
    displayDuration: '120 horas · ~14 semanas',
    heroSubtitle: 'Programa de preparación de credencial de 120 horas por Zoom',
    locationLabel: 'Zoom (Clase virtual)',
    location_details: 'Zoom (Clase virtual)',
    sections: [
      {
        title: '¿Quién debe asistir?',
        content:
          'Directores de centros de cuidado infantil, maestros, asistentes de maestros, proveedores de cuidado infantil familiar, asistentes de proveedores, proveedores alternos, asistentes alternos y visitadores del hogar.',
      },
      {
        title: 'Acerca de la credencial CDA',
        content:
          'La credencial CDA es administrada por el Consejo para el Reconocimiento Profesional en Washington D.C. Obtener una credencial CDA puede ayudarle a mejorar sus habilidades y confianza al trabajar con niños, y también puede aumentar sus oportunidades de avance en el campo de la primera infancia al hacerle elegible para puestos como Director, Líder de grupo y Asistente de maestro.',
      },
      EIP_HELP_SECTION_ES,
    ],
  },
  'Child Development and Learning (CDL)': {
    title: 'Desarrollo y Aprendizaje Infantil (CDL)',
    description: `Esta capacitación cubre las nueve categorías de entrenamiento requeridas por la Oficina de Servicios para Niños y Familias del Estado de Nueva York para renovar una licencia de Cuidado Infantil Familiar o Cuidado Infantil Escolar.

Esta clase cumple con las 30 horas de capacitación exigidas por OCFS, dentro de las diez categorías de entrenamiento.`,
    displayDuration: '30 horas',
    heroSubtitle: 'Capacitación de renovación de licencia OCFS por Zoom',
    locationLabel: 'Zoom (Clase virtual)',
    location_details: 'Zoom (Clase virtual)',
    sections: [
      {
        title: '¿Quién debe asistir?',
        content:
          'Personal de Cuidado Infantil Familiar o Cuidado Infantil Escolar que necesite renovar su licencia o registro.',
      },
      {
        title: 'Temas de capacitación',
        content:
          'Esta clase cumple con las 30 horas de capacitación exigidas por OCFS, dentro de las diez categorías de entrenamiento. Para la licencia, cumplimiento y renovación.',
        numberedList: true,
        listItems: [
          'principios del desarrollo infantil, con enfoque en las etapas de desarrollo de los grupos de edad para los que el programa brinda cuidado',
          'necesidades nutricionales y de salud de bebés y niños',
          'desarrollo de programas de cuidado infantil',
          'procedimientos de seguridad y protección',
          'mantenimiento y administración de registros comerciales',
          'identificación y prevención del abuso y maltrato infantil',
          'estatutos y regulaciones relacionados con el cuidado infantil',
          'estatutos y regulaciones relacionados con el abuso y maltrato infantil',
          'educación e información sobre la identificación, diagnóstico y prevención del síndrome del bebé sacudido',
          'experiencias adversas en la infancia, centradas en comprender el trauma y fomentar la resiliencia',
        ],
      },
      EIP_HELP_SECTION_ES,
    ],
  },
  'Health and Safety Competency Training': {
    title: 'Capacitación en Competencias de Salud y Seguridad',
    description: `Esta es la capacitación obligatoria de 15 horas del Estado de Nueva York, competencias para convertirse en proveedor de cuidado infantil familiar o grupal familiar. Esta capacitación satisface las quince horas iniciales de capacitación requeridas para nuevos empleados de cuidado infantil y también puede tomarse como parte de la Capacitación Obligatoria de 30 Horas del Estado. Los cupos para esta clase son limitados. Se requiere preinscripción.`,
    displayDuration: '15 horas · 3 sábados',
    heroSubtitle: 'Competencias iniciales de salud y seguridad exigidas por el estado de NY para proveedores de cuidado infantil',
    locationLabel: '1110 East 93rd Street, Brooklyn, NY 11216',
    location_details: '1110 East 93rd Street, Brooklyn, NY 11216',
    sections: [
      {
        title: '¿Quién debe asistir?',
        content:
          'Proveedores de cuidado infantil en formación que buscan una licencia o registro Familiar o Grupal Familiar, proveedores existentes que cambian o renuevan su licencia y nunca han tomado la capacitación de salud y seguridad. Los asistentes de guardería también pueden tomar esta clase como parte de sus requisitos de capacitación inicial.',
      },
      {
        title: 'Acerca de esta capacitación',
        content:
          'Esta es la capacitación obligatoria de 15 horas del Estado de Nueva York, competencias para convertirse en proveedor de cuidado infantil familiar o grupal familiar. Esta capacitación satisface las quince horas iniciales de capacitación requeridas para nuevos empleados de cuidado infantil y también puede tomarse como parte de la Capacitación Obligatoria de 30 Horas del Estado. Los cupos para esta clase son limitados. Se requiere preinscripción.',
      },
      {
        title: 'Horario y tarifas',
        content:
          'Este es un curso de tres días en 1110 East 93rd St, Brooklyn, NY. Tarifa del curso: $300.',
      },
      EIP_HELP_SECTION_ES,
    ],
  },
  'American Red Cross Adult and Pediatric First Aid/CPR/AED': {
    title: 'Primeros Auxilios/RCP/DEA Pediátrico y para Adultos de la Cruz Roja Americana',
    description: `Esta clase certificada por la Cruz Roja Americana le proporcionará los conocimientos y habilidades necesarios para enfrentar emergencias respiratorias y cardíacas en bebés y niños, hasta que llegue personal médico avanzado y tome el control. Los participantes recibirán certificación de la Cruz Roja Americana por dos años.

Las clases se imparten en 1110 East 93rd St, Brooklyn, NY. Tarifa del curso: $135.`,
    displayDuration: '1 día · 5 horas',
    heroSubtitle: 'Certificación de la Cruz Roja Americana — válida por dos años',
    locationLabel: '1110 East 93rd Street, Brooklyn, NY 11216',
    location_details: '1110 East 93rd Street, Brooklyn, NY 11216',
    sections: [
      {
        title: '¿Quién debe asistir?',
        content: 'Todas las personas que tienen niños o trabajan con niños.',
      },
      {
        title: 'Acerca de esta certificación',
        content:
          'Esta clase certificada por la Cruz Roja Americana le proporcionará los conocimientos y habilidades necesarios para enfrentar emergencias respiratorias y cardíacas en bebés y niños, hasta que llegue personal médico avanzado y tome el control. Los participantes recibirán certificación de la Cruz Roja Americana por dos años.',
      },
      {
        title: 'Horario y tarifas',
        content:
          'Clases de un día en 1110 East 93rd St, Brooklyn, NY de 10:00 AM a 3:00 PM. Tarifa del curso: $135. Se requiere preinscripción.',
      },
      EIP_HELP_SECTION_ES,
    ],
    onSiteTraining: {
      title: 'Capacitación en el sitio para empresas y grupos',
      intro:
        'Un instructor certificado de la Cruz Roja Americana acudirá a su programa o grupo entre semana, por las tardes o los sábados.',
      listItems: [
        'Se requiere un grupo de al menos 5 participantes.',
        'Llame para más información',
      ],
    },
  },
  'Orientation for New Family Child Care Providers': {
    title: 'Orientación para Nuevos Proveedores de Cuidado Infantil Familiar',
    description: `Esta orientación ayuda a los aspirantes a proveedores de cuidado infantil familiar a comprender lo que implica iniciar un programa de cuidado infantil familiar con licencia.

Las sesiones se realizan por Zoom. Esta clase es gratuita. Se requiere preinscripción.`,
    displayDuration: 'Gratis · 1 sesión',
    heroSubtitle: 'Orientación gratuita para aspirantes a proveedores de cuidado infantil familiar por Zoom',
    locationLabel: 'Zoom (Clase virtual)',
    location_details: 'Zoom (Clase virtual)',
    sections: [
      {
        title: '¿Quién debe asistir?',
        content: 'Aspirantes a proveedores de cuidado infantil familiar.',
      },
      {
        title: 'Lo que aprenderá',
        content: 'Esta orientación ayuda a los aspirantes a proveedores de cuidado infantil familiar a:',
        listItems: [
          'Comprender el proceso para convertirse en proveedor de cuidado infantil con licencia.',
          '¿Qué es un proveedor de cuidado infantil familiar?',
          'Beneficios de iniciar su propio negocio de cuidado infantil familiar.',
          'Proceso de solicitud y requisitos.',
          'Recursos disponibles para proveedores nuevos y existentes de cuidado infantil familiar.',
        ],
      },
      {
        title: 'Horario y tarifas',
        content:
          'Orientación de una sesión por Zoom. Esta clase es gratuita. Se requiere preinscripción.',
      },
    ],
  },
};

/** Short labels for footer / compact displays */
export const FOOTER_CLASS_LABELS_ES = {
  'Child Development Associate (CDA)': 'CDA',
  'Child Development and Learning (CDL)': 'CDL',
  'Health and Safety Competency Training': 'Salud y Seguridad',
  'American Red Cross Adult and Pediatric First Aid/CPR/AED': 'RCP / Primeros Auxilios',
  'Orientation for New Family Child Care Providers': 'Orientación',
};

export const getSpanishClassContent = (englishTitle) => {
  if (!englishTitle) return null;
  return CLASS_CONTENT_ES[englishTitle] || null;
};
